import {Link, Text} from '@primer/react-brand/esm'
import type {Icon} from '@primer/octicons-react'

import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'

import {useUrlContext} from '../../contexts/UrlContext'

import styles from './NavLink.module.css'

export type NavLinkType = {
  className?: string
  context?: string
  external?: boolean
  icon?: Icon
  label?: string
  render?: boolean
  subtitle?: string
  title: string
  url: string
  withArrow?: boolean
}

export function NavLink(props: NavLinkType) {
  const {shouldUseDotcomLinks} = useUrlContext()

  const {
    className,
    context,
    external = false,
    icon: IconComponent,
    label,
    render = true,
    subtitle,
    title,
    url,
    withArrow = false,
  } = props

  if (render === false) {
    return null
  }

  const resolvedUrl = shouldUseDotcomLinks && url.startsWith('/') ? `https://github.com${url}` : url

  const analyticsAttrs = {
    ...getAnalyticsEvent({
      action: title,
      tag: 'link',
      context: context || '',
      location: 'navbar',
    }),
  }

  if (external) {
    return (
      <Link href={resolvedUrl} {...analyticsAttrs} arrowDirection="none" isExternal target="_blank" rel="noreferrer">
        {title}
      </Link>
    )
  }

  if (withArrow) {
    return (
      <Link href={resolvedUrl} {...analyticsAttrs} arrowDirection="end">
        {title}
      </Link>
    )
  }

  if (subtitle || IconComponent || label) {
    return (
      <Link href={resolvedUrl} {...analyticsAttrs} arrowDirection="none" className={styles.link}>
        <Text as="span" className={styles.title} size="200" weight="medium">
          {IconComponent ? <IconComponent size={16} className={styles.icon} /> : null}

          {title}

          {label ? <sup className={styles.label}>{label}</sup> : null}
        </Text>

        {subtitle ? (
          <Text as="span" size="200" variant="muted" className={styles.subtitle}>
            {subtitle}
          </Text>
        ) : null}
      </Link>
    )
  }

  if (className) {
    return (
      <a href={resolvedUrl} {...analyticsAttrs} className={className}>
        {title}
      </a>
    )
  }

  return (
    <Link href={resolvedUrl} {...analyticsAttrs} arrowDirection="none">
      {title}
    </Link>
  )
}
